/**
 * Withdrawal and exit queue operations for StakeWise integration.
 *
 * @remarks
 * This file contains internal implementations for withdrawal-related operations
 * using StakeWise SDK request methods. All methods return abortable promises
 * for better user experience control.
 *
 * @internal
 * @packageDocumentation
 */
import { ETHEREUM_BD_MAINNET_VAULT, normalizeEthVaultAddress, } from "../../../../shared/vault-addresses";
/**
 * Internal implementation for retrieving exit queue positions.
 *
 * @remarks
 * Retrieves user's exit queue positions using StakeWise SDK's `vault.getExitQueuePositions`
 * request method. Returns withdrawal queue data including duration estimates, withdrawable
 * amounts, and position status for tracking withdrawal progress.
 *
 * **StakeWise SDK Integration:**
 * - Uses `vault.getExitQueuePositions()` request method
 * - Returns abortable promise for UX control
 * - Supports filtering by claimed/unclaimed status
 * - Provides positions in format required for claiming
 *
 * **Exit Queue Data Structure:**
 * - `requests`: Array of all exit requests with detailed metadata
 * - `positions`: Positions formatted for claiming operations
 * - `total`: Total queued assets in wei
 * - `duration`: Approximate time until assets can be collected (seconds)
 * - `withdrawable`: Assets currently available for withdrawal
 *
 * **Duration Interpretation:**
 * - `null`: Time still being calculated
 * - `0`: Assets are available and can be collected immediately
 * - `> 0`: Approximate seconds until assets become available
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance
 * @param logger - Logger for operation tracking
 * @param request - Request parameters with user and vault info
 * @returns Promise resolving to exit queue positions data
 * @throws {Error} When vault address invalid or request fails
 *
 * @see {@link https://sdk.stakewise.io/vault/requests/getexitqueuepositions} StakeWise SDK getExitQueuePositions Documentation
 * @see {@link https://sdk.stakewise.io/boost/requests/getqueueposition} StakeWise SDK boost getQueuePosition Documentation
 *
 * @internal
 */
export async function _getExitQueuePositions(stakewiseSDK, logger, request) {
    const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
    if (!vaultAddress)
        throw new Error("Vault address not found");
    logger.debug("Retrieving exit queue positions from StakeWise SDK", {
        vaultAddress,
        userAddress: request.userAddress,
        isClaimed: request.isClaimed,
    });
    const positions = await stakewiseSDK.vault.getExitQueuePositions({
        vaultAddress,
        userAddress: request.userAddress,
        isClaimed: request.isClaimed,
    });
    logger.debug("Successfully retrieved exit queue positions from StakeWise SDK", {
        positionCount: positions.positions.length,
        requestCount: positions.requests.length,
        totalWithdrawable: positions.withdrawable,
    });
    return positions;
}
/**
 * Internal implementation for retrieving maximum withdrawable amount.
 *
 * @remarks
 * Calculates the maximum amount a user can withdraw while maintaining required
 * collateralization for osToken backing using StakeWise SDK's `vault.getMaxWithdraw`
 * request method. Accounts for vault liquidity and osToken position requirements.
 *
 * **StakeWise SDK Integration:**
 * - Uses `vault.getMaxWithdraw()` request method
 * - Returns abortable promise for request control
 * - Accounts for osToken collateralization requirements
 * - Integrates with osToken position data for LTV calculations
 *
 * **Calculation Logic:**
 * - No osTokens minted: Can withdraw full staked amount (subject to vault limits)
 * - With osTokens minted: Must maintain sufficient collateral based on LTV percentage
 * - LTV percentage specified in basis points (9000 = 90%)
 * - Formula: `maxWithdraw = stakedAssets - (mintedAssets / ltvPercent * 10000)`
 *
 * **Real-time Factors:**
 * - Current vault liquidity and available capacity
 * - Pending exit queue positions and processing status
 * - Protocol-specific withdrawal limits and constraints
 * - osToken health factor and liquidation thresholds
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance
 * @param logger - Logger for operation tracking
 * @param request - Request with LTV and asset parameters
 * @returns Promise resolving to maximum withdrawable amount in wei
 * @throws {Error} When vault address invalid or SDK request fails
 * @throws {Error} When LTV parameters are invalid or outside protocol bounds
 *
 * @see {@link https://sdk.stakewise.io/vault/requests/getmaxwithdraw} StakeWise SDK getMaxWithdraw Documentation
 * @see {@link https://sdk.stakewise.io/osToken/requests/getposition} StakeWise SDK osToken getPosition Documentation
 *
 * @example
 * **Basic Usage with No osTokens**
 * ```typescript
 * const maxWithdraw = await _getMaxWithdraw(sdk, logger, {
 *   vaultAddress: "0x...",
 *   ltvPercent: 0n, // No osTokens minted
 *   mintedAssets: 0n,
 *   stakedAssets: BigInt("2000000000000000000") // 2 ETH staked
 * });
 * // Result: Can withdraw full 2 ETH (subject to vault liquidity)
 * ```
 *
 * @example
 * **Usage with osToken Collateralization**
 * ```typescript
 * const maxWithdraw = await _getMaxWithdraw(sdk, logger, {
 *   vaultAddress: "0x...",
 *   ltvPercent: 9000n // 90% LTV
 *   mintedAssets: BigInt("1000000000000000000"), // 1 osETH minted
 *   stakedAssets: BigInt("2000000000000000000")  // 2 ETH staked
 * });
 * // Result: Limited withdrawal to maintain 90% LTV for 1 osETH
 * ```
 *
 * @internal
 */
export async function _getMaxWithdraw(stakewiseSDK, logger, request) {
    const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
    if (!vaultAddress)
        throw new Error("Vault address not found");
    logger.debug("Retrieving maximum withdrawable amount from StakeWise SDK", {
        vaultAddress,
        ltvPercent: request.ltvPercent.toString(),
        mintedAssets: request.mintedAssets.toString(),
        stakedAssets: request.stakedAssets.toString(),
    });
    const maxWithdraw = await stakewiseSDK.vault.getMaxWithdraw({
        vaultAddress,
        ltvPercent: request.ltvPercent,
        mintedAssets: request.mintedAssets,
        stakedAssets: request.stakedAssets,
    });
    logger.debug("Successfully retrieved maximum withdrawable amount from StakeWise SDK", {
        maxWithdraw: maxWithdraw.toString(),
    });
    return maxWithdraw;
}
//# sourceMappingURL=data:application/json;base64,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